import { useEffect, useRef, useState } from "react";
import { gisMarkers, type GisMarker } from "@/mocks/gis";
import MarkerPopup from "./MarkerPopup";

declare global {
  interface Window {
    L: typeof import("leaflet");
  }
}

interface Props {
  filterTypes: string[];
  searchText: string;
}

const markerColors: Record<string, string> = {
  resident: "#22c55e",
  commercial: "#E8531A",
  alarm: "#ef4444",
  station: "#3b82f6",
  pipeline: "#8b5cf6",
  valve: "#f59e0b",
  pressure: "#06b6d4",
};

const markerIcons: Record<string, string> = {
  resident: "🏠",
  commercial: "🏢",
  alarm: "⚠️",
  station: "🔧",
  pipeline: "📍",
  valve: "🔴",
  pressure: "📊",
};

const pressurePoints = [
  { id: "P001", lat: 39.92, lng: 116.42, name: "朝阳调压站A", pressure: 0.35, status: "正常", maxPressure: 0.4 },
  { id: "P002", lat: 39.91, lng: 116.38, name: "海淀调压站B", pressure: 0.28, status: "正常", maxPressure: 0.4 },
  { id: "P003", lat: 39.93, lng: 116.45, name: "东城监测点C", pressure: 0.41, status: "超压告警", maxPressure: 0.4 },
  { id: "P004", lat: 39.89, lng: 116.40, name: "丰台调压站D", pressure: 0.22, status: "正常", maxPressure: 0.4 },
];

const valvePoints = [
  { id: "V001", lat: 39.905, lng: 116.415, name: "主干阀门V001", status: "开启", type: "球阀" },
  { id: "V002", lat: 39.915, lng: 116.395, name: "支线阀门V002", status: "关闭", type: "闸阀" },
  { id: "V003", lat: 39.925, lng: 116.435, name: "紧急截断阀V003", status: "开启", type: "截断阀" },
  { id: "V004", lat: 39.895, lng: 116.425, name: "调压阀V004", status: "开启", type: "调压阀" },
];

export default function GisMap2D({ filterTypes, searchText }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<unknown>(null);
  const markersRef = useRef<unknown[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<GisMarker | null>(null);
  const [selectedPressure, setSelectedPressure] = useState<typeof pressurePoints[0] | null>(null);
  const [selectedValve, setSelectedValve] = useState<typeof valvePoints[0] | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showPressure, setShowPressure] = useState(true);
  const [showValves, setShowValves] = useState(true);
  const [showPipeline, setShowPipeline] = useState(true);

  const filteredMarkers = gisMarkers.filter(m => {
    const typeMatch = filterTypes.length === 0 || filterTypes.includes(m.type);
    const searchMatch = !searchText || m.name.includes(searchText) || m.address.includes(searchText);
    return typeMatch && searchMatch;
  });

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const loadLeaflet = async () => {
      if (!window.L) {
        await new Promise<void>((resolve) => {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
          document.head.appendChild(link);

          const script = document.createElement("script");
          script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
          script.onload = () => resolve();
          document.head.appendChild(script);
        });
      }

      const L = window.L;
      if (!mapRef.current) return;

      const map = L.map(mapRef.current, {
        center: [39.9042, 116.4074],
        zoom: 12,
        zoomControl: false,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap",
        maxZoom: 19,
      }).addTo(map);

      L.control.zoom({ position: "bottomright" }).addTo(map);

      mapInstanceRef.current = map;
      setMapLoaded(true);
    };

    loadLeaflet();

    return () => {
      if (mapInstanceRef.current) {
        (mapInstanceRef.current as { remove: () => void }).remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current) return;
    const L = window.L;
    const map = mapInstanceRef.current as {
      addLayer: (l: unknown) => void;
      removeLayer: (l: unknown) => void;
    };

    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    filteredMarkers.forEach(marker => {
      const color = markerColors[marker.type] || "#666";
      const isAlarm = marker.type === "alarm" || marker.alarmLevel === "紧急";

      const icon = L.divIcon({
        className: "",
        html: `<div style="
          width:${isAlarm ? "36px" : "28px"};height:${isAlarm ? "36px" : "28px"};
          background:${color};border:2px solid white;border-radius:50%;
          display:flex;align-items:center;justify-content:center;
          font-size:${isAlarm ? "15px" : "12px"};
          box-shadow:0 2px 6px rgba(0,0,0,0.2);cursor:pointer;
          ${isAlarm ? "animation:pulse 1.5s infinite;" : ""}
        ">${markerIcons[marker.type] || "📍"}</div>
        ${isAlarm ? `<style>@keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.2)}}</style>` : ""}`,
        iconSize: [isAlarm ? 36 : 28, isAlarm ? 36 : 28],
        iconAnchor: [isAlarm ? 18 : 14, isAlarm ? 18 : 14],
      });

      const leafletMarker = L.marker([marker.lat, marker.lng], { icon })
        .addTo(mapInstanceRef.current as Parameters<typeof L.marker>[1] extends object ? object : never);

      (leafletMarker as { on: (e: string, h: () => void) => void }).on("click", () => {
        setSelectedMarker(marker);
        setSelectedPressure(null);
        setSelectedValve(null);
      });

      markersRef.current.push(leafletMarker);
    });

    if (showPressure) {
      pressurePoints.forEach(p => {
        const isAlert = p.status !== "正常";
        const icon = L.divIcon({
          className: "",
          html: `<div style="
            width:32px;height:32px;background:${isAlert ? "#ef4444" : "#06b6d4"};
            border:2px solid white;border-radius:8px;
            display:flex;align-items:center;justify-content:center;
            font-size:11px;color:white;font-weight:bold;cursor:pointer;
            box-shadow:0 2px 6px rgba(0,0,0,0.2);
            ${isAlert ? "animation:pulse 1s infinite;" : ""}
          ">${p.pressure.toFixed(2)}</div>`,
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });
        const m = L.marker([p.lat, p.lng], { icon })
          .addTo(mapInstanceRef.current as Parameters<typeof L.marker>[1] extends object ? object : never);
        (m as { on: (e: string, h: () => void) => void }).on("click", () => {
          setSelectedPressure(p);
          setSelectedMarker(null);
          setSelectedValve(null);
        });
        markersRef.current.push(m);
      });
    }

    if (showValves) {
      valvePoints.forEach(v => {
        const isOpen = v.status === "开启";
        const icon = L.divIcon({
          className: "",
          html: `<div style="
            width:26px;height:26px;background:${isOpen ? "#22c55e" : "#ef4444"};
            border:2px solid white;border-radius:4px;
            display:flex;align-items:center;justify-content:center;
            font-size:12px;cursor:pointer;
            box-shadow:0 2px 6px rgba(0,0,0,0.2);
          ">🔧</div>`,
          iconSize: [26, 26],
          iconAnchor: [13, 13],
        });
        const m = L.marker([v.lat, v.lng], { icon })
          .addTo(mapInstanceRef.current as Parameters<typeof L.marker>[1] extends object ? object : never);
        (m as { on: (e: string, h: () => void) => void }).on("click", () => {
          setSelectedValve(v);
          setSelectedMarker(null);
          setSelectedPressure(null);
        });
        markersRef.current.push(m);
      });
    }
  }, [mapLoaded, filteredMarkers, showPressure, showValves]);

  return (
    <div className="relative w-full h-full">
      <div ref={mapRef} className="w-full h-full rounded-xl overflow-hidden" />

      {/* Layer Controls */}
      <div className="absolute top-3 right-3 z-[999] bg-white rounded-xl border border-gray-100 p-3 space-y-2">
        <p className="text-xs font-semibold text-gray-600 mb-2">图层控制</p>
        {[
          { key: "pressure", label: "压力监测", state: showPressure, set: setShowPressure, color: "bg-cyan-500" },
          { key: "valves", label: "阀门状态", state: showValves, set: setShowValves, color: "bg-green-500" },
          { key: "pipeline", label: "管网拓扑", state: showPipeline, set: setShowPipeline, color: "bg-orange-500" },
        ].map(item => (
          <div key={item.key} className="flex items-center gap-2 cursor-pointer" onClick={() => item.set(!item.state)}>
            <div className={`w-3 h-3 rounded-full ${item.state ? item.color : "bg-gray-200"}`}></div>
            <span className={`text-xs ${item.state ? "text-gray-700" : "text-gray-400"}`}>{item.label}</span>
          </div>
        ))}
      </div>

      {/* Pressure Legend */}
      {showPressure && (
        <div className="absolute bottom-16 right-3 z-[999] bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-xs font-semibold text-gray-600 mb-2">压力监测图例</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="w-6 h-4 rounded bg-cyan-500 flex items-center justify-center text-white text-[8px] font-bold">0.3</div>
              <span className="text-xs text-gray-500">正常（MPa）</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-4 rounded bg-red-500 flex items-center justify-center text-white text-[8px] font-bold">0.4</div>
              <span className="text-xs text-gray-500">超压告警</span>
            </div>
          </div>
        </div>
      )}

      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-[#E8531A] border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-500">地图加载中...</span>
          </div>
        </div>
      )}

      {selectedMarker && (
        <MarkerPopup marker={selectedMarker} onClose={() => setSelectedMarker(null)} />
      )}

      {selectedPressure && (
        <div className="absolute bottom-4 left-4 z-[999] bg-white rounded-xl border border-gray-100 p-4 w-64">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${selectedPressure.status === "正常" ? "bg-cyan-500" : "bg-red-500 animate-pulse"}`}></div>
              <span className="text-sm font-semibold text-gray-800">{selectedPressure.name}</span>
            </div>
            <button onClick={() => setSelectedPressure(null)} className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
              <i className="ri-close-line text-gray-400 text-xs"></i>
            </button>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">当前压力</span>
              <span className={`font-bold ${selectedPressure.status !== "正常" ? "text-red-600" : "text-cyan-600"}`}>{selectedPressure.pressure} MPa</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">最大压力</span>
              <span className="font-medium text-gray-700">{selectedPressure.maxPressure} MPa</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${selectedPressure.status !== "正常" ? "bg-red-500" : "bg-cyan-500"}`}
                style={{ width: `${Math.min((selectedPressure.pressure / selectedPressure.maxPressure) * 100, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">状态</span>
              <span className={`font-medium ${selectedPressure.status !== "正常" ? "text-red-600" : "text-green-600"}`}>{selectedPressure.status}</span>
            </div>
          </div>
        </div>
      )}

      {selectedValve && (
        <div className="absolute bottom-4 left-4 z-[999] bg-white rounded-xl border border-gray-100 p-4 w-64">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${selectedValve.status === "开启" ? "bg-green-500" : "bg-red-500"}`}></div>
              <span className="text-sm font-semibold text-gray-800">{selectedValve.name}</span>
            </div>
            <button onClick={() => setSelectedValve(null)} className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
              <i className="ri-close-line text-gray-400 text-xs"></i>
            </button>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">阀门类型</span>
              <span className="font-medium text-gray-700">{selectedValve.type}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">当前状态</span>
              <span className={`font-bold ${selectedValve.status === "开启" ? "text-green-600" : "text-red-600"}`}>{selectedValve.status}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">编号</span>
              <span className="font-mono text-gray-600">{selectedValve.id}</span>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button className="flex-1 border border-gray-200 text-gray-600 py-1.5 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">远程操控</button>
            <button className="flex-1 bg-[#E8531A] text-white py-1.5 rounded-lg text-xs cursor-pointer whitespace-nowrap">
              {selectedValve.status === "开启" ? "关闭阀门" : "开启阀门"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
